<script lang="ts">
  import { visualizationDataStore } from "../data-views/stores";
  import FaArrowsAltH from "svelte-icons/fa/FaArrowsAltH.svelte";
  import FaArrowsAltV from "svelte-icons/fa/FaArrowsAltV.svelte";
  import FaExpand from "svelte-icons/fa/FaExpand.svelte";
  import FaRegTimesCircle from "svelte-icons/fa/FaRegTimesCircle.svelte";
  import Button from "./button.svelte";
  import Loader from "./loader.svelte";
  import Modal from "./modal.svelte";
  import { t } from "../translations/i18n";

  export let showLoader: boolean = false;
  export let selectorDisabled: boolean = true;
  export let visualizationId: string;
  export let showAdditionalInfo: boolean = false;

  let isWide: boolean = true;
  let isFullScreen: boolean = false;
  let showSelector: boolean = false;
  let showModal: boolean = false;

  $: if (showLoader) {
    showSelector = false;
  }

  const selectorHandler = (): void => {
    showSelector = !showSelector;
  };

  const sizeHandler = (): void => {
    isWide = !isWide;
    handleResizing();
  };

  const fullScreenHandler = (): void => {
    isFullScreen = !isFullScreen;
    handleResizing();
  };

  const handleResizing = (): void => {
    container.style.transition = "width 400ms ease";
    // Wait for the transition to finish and dispatch resize event for data
    // visualization components to update their size
    setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
      // Do not use transition on window resize to as visualization
      // components update their size immediately
      container.style.removeProperty("transition");
    }, 401);
  };

  const removeHandler = (): void => {
    showModal = true;
  };

  const modalCancelHandler = (): void => {
    showModal = false;
  };

  const removeVisualization = (): void => {
    visualizationDataStore.removeVisualization(visualizationId);
    container.style.transition = "height 400ms ease";
    container.style.height = "0px";
    setTimeout(() => {
      container?.parentNode?.removeChild(container);
    }, 200);
  };
  
  let container: HTMLElement;
</script>

<div
  bind:this={container}
  class="container {isWide ? 'wide' : ''} {isFullScreen ? 'full-screen' : ''}"
>
  <Loader visible={showLoader} />

  <header>
    <nav>
      <div class="select-content-button">
        <Button
          handler={selectorHandler}
          title="Select content"
          disabled={selectorDisabled}
        >
          <FaArrowsAltV slot="icon" />
          <svelte:fragment slot="text">{$t("Select content")}</svelte:fragment>
        </Button>
      </div>
      <div class="buttons">
        <slot name="buttons" />
        {#if !isFullScreen}
          <div class="icon resize" on:click={sizeHandler} title="Resize">
            <FaArrowsAltH />
          </div>
        {/if}
        <div class="icon" on:click={fullScreenHandler} title="Full screen">
          <FaExpand />
        </div>
        {#if !isFullScreen}
          <div class="icon remove" on:click={removeHandler} title="Remove">
            <FaRegTimesCircle />
          </div>
        {/if}
      </div>
    </nav>
  </header>
  <div class="main">
    <div>
      <h1>{$t("Unavailable Data")}!</h1>
      {#if showAdditionalInfo}
        <slot name="additional-information"></slot>
      {/if}
      <Button
        handler={removeVisualization}
        title="Close unavailable chart"
      >
        <svelte:fragment slot="text">{$t("Remove Container")}</svelte:fragment>
      </Button>
    </div>
  </div>

  {#if showModal}
    <Modal okHandler={removeVisualization} cancelHandler={modalCancelHandler}>
      <svelte:fragment slot="content">
        <h2>{$t("Are you sure you want to remove this visualization?")}</h2>
        <p>
          {$t("This action cannot be undone. All the data associated with this visualization will be lost.")}
        </p>
      </svelte:fragment>
    </Modal>
  {/if}
</div>

<style lang="scss">
  @import "../scrollbars";

  .container {
    --header-height: 4.5rem;

    width: var(--visualization-width);
    height: var(--visualization-height);
    position: relative;
    color: var(--color-gray-dark);
    background-color: var(--color-white);
    overflow: auto;

    header {
      position: sticky;
      height: var(--header-height);
      top: 0;
      left: 0;
      z-index: 10;
      display: flex;
      align-items: center;
      background-color: var(--color-white);

      nav {
        flex: 1;
        display: flex;
        justify-content: space-between;
        gap: 2rem;
        margin: 0 2rem;

        .select-content-button {
          :global(.text) {
            @media screen and (max-width: 500px) {
              display: none;
            }
          }
        }

        .buttons {
          display: flex;
          align-items: center;
          gap: 1rem;
          color: var(--color-gray);

          // affect inside the buttons slot
          :global(.icon) {
            width: 1.25rem;
            height: 1.25rem;
          }

          .resize {
            display: var(--display-on-wide, flex);
          }

          .remove {
            color: var(--color-tertiary);
          }
          
        }
      }
    }

    .main {
      position: relative;
      overflow: auto;
      height: calc(var(--visualization-height) - var(--header-height));
      box-sizing: border-box;
      padding: 0 2rem 1rem;
      display: flex;
      justify-content: center;
      align-items: center;
      text-align: center;

      @include scrollbars;
      
      > div {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        height: 100%;
  }
    }

    &.wide {
      width: 100%;
    }

    &.full-screen {
      position: fixed;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      width: 100%;
      height: 100%;
      z-index: 100;
      overflow: hidden;

      .main {
        height: calc(100% - var(--header-height));
      }
    }
    @include scrollbars;
  }
</style>
